/** 混入方法 ::: 
 *  @mixin mixFormConfig 表单项属性解构混入
 */
import { mixFormConfig } from "@/mixins/common/mix_form_config.js";
/** 字典数据 ::: 
 *  @function formValidatorRuleFunction 获取表单项校验方法
 */
import { formValidatorRuleFunction } from "@/assets/js/config_dictionary.js"; 
/** 工具方法 :::
 *  @function makeComputedGroup 生成计算属性组方法
 */
import { makeComputedGroup } from "@/utils/computed_utils.js";


/** 枚举数据 ::: 
 *  @enum {Number} assembleTypeEnum 事件组合类型 {10:条件同时成立, 20:满足任意条件成立}
 *  @enum {Number} eventActionEnum 事件行为类型 {1:显示, 2:复制, 3:删除, 4:解禁, 5:禁用, 6:隐藏}
 */
const assembleTypeEnum = { "and":10, "or":20 };
const eventActionEnum = { "display":1, "copy":2, "delete":3, "enable":4, "disable":5, "hide":6 };
/** @name PreviewItemTemplate 子级混入
 *  @author 王汝鹏
 *  @version 2019-11-29
 */
export const mixPreviewItemTemplateChild = {
  mixins: [ mixFormConfig ],
  inject: [
    'preview_item_template_store',
    'preview_item_template_node_action_manager',
  ],
  props: {
    parent: { type: Array, default: () => [] },
    formNode: { type: Object, required: true }
  },
  computed: {
    /** 计算属性说明 ::: 
     *  @return {Object} eventMap 事件字典
     *  @return {Object} formMap 表单项字典
     *  @return {Object} formModels 表单填写内容
     *  @return {Object} variableMap 变量设置字典
     */
    ...makeComputedGroup("preview_item_template_store", {
      eventMap: { get: "eventMap" },
      formMap: { get: "formMap" },
      formModels: "formModels",
      variableMap: "variableMap",
      toggleClearModelState: "toggleClearModelState"
    }),
    /** 管理者说明 ::: 
     *  @return {Function} nodeActionManager 节点行为管理者
     */
    nodeActionManager() { return this.preview_item_template_node_action_manager; },
    /** @name 当前表单项的变量值
     *  @return {Any} 当前表单项的变量值
     */
    currentModel: { 
      get() { return this.formModels ? this.formModels[this.model] : undefined; },
      set(newValue) {
        /**
         * 每次表单项绑定值发生改变，需要去变量字典中寻找有没有相关设置，
         * 如果有，需要将将他的值赋值给与他对应的表单项变量，
         * 如果没有，直接更新表单添加数据中该表单项变量的值
        */
        let { variableMap } = this;
        let currentVariable = variableMap[this.model];
       
        if ( currentVariable ) {
          // 将当前表单项的值赋值给与他对应的表单项变量
          currentVariable.forEach( el => {
            this.$set(this.formModels, el, newValue);
          })
        } else {
          this.$set(this.formModels, this.model, newValue);
        }
      }
    },
    /** @name 表单项校验规则
     *  @return {Array<Object>} 校验规则
     */
    validatorRule() {
      let { validator } = this;
      let { isRequire } = validator;

      let rules = [];
      let validatorTrigger = ['input', 'textarea'].includes(this.formType) ? 'blur' : 'change'
      // 必填校验规则
      if (isRequire) rules.push({ required:true, message:'内容必填', trigger:validatorTrigger });
      let validatorMethod = formValidatorRuleFunction(validator);
      // 其它内容校验规则
      if (validatorMethod) rules.push({ validator:validatorMethod, trigger:validatorTrigger });
      return rules;
    },
    /** @name 当前表单项触发事件 类型-事件 字典
     *  @return {Object<String, String>}  {key:事件类型, value:事件id}
     *  @author 王汝鹏
     *  @version 2019-12-27
     */
    currentEmitEventMap() {
      // 当前表单项id
      let formId = this.formNode.id;
      // 表单项触发事件配置
      let { emit } = this.formEventConfig;

      let typeEmitMap = {};
      // 遍历表单项所有触发事件
      for (let e of emit) {
        // 事件配置 之 触发事件配置
        let { emit:eventEmits } = this.eventMap[e];
        // 找到当前表单项触发配置
        let config = eventEmits.find(i => (i.form === formId));
        let { type } = config;
        Reflect.has(typeEmitMap, type) 
          ? typeEmitMap[type].push(e)
          : typeEmitMap[type] = [e];
      }
      return typeEmitMap;
    },
    /** @name 当前表单项 触发事件状态 字典 
     *  @return {Object<String, Function<Boolean>>} {key:事件id, value:更新事件状态的方法}
     *  @author 王汝鹏
     *  @version 2019-12-27
     */
    currentEventStatusMap() {
      let that = this;
      // 表单项监听事件配置
      let { on } = this.formEventConfig;

      let eventStatusMap = {};
      // 遍历监听事件，挂载事件
      for (let e of on) {
        // 事件配置: {type:事件组合类型(并且、或者、...), emit:事件触发配置，on:事件监听配置}
        let { type, emit, on } = this.eventMap[e];
        // 事件状态: {state:事件状态, stateTarget:事件目标状态} 
        // 当事件触发时，state 与 stateTarget 值进行比较，判断是否触发行为
        let state = {};
        let stateTarget = {};
        // 初始化事件状态值
        for (let { form, value } of emit) { 
          state[form] = undefined;
          stateTarget[form] = value;
        }
        
        // 更新事件状态方法
        eventStatusMap[e] = function(form, value) {
          state[form] = value;
          switch(type) {
            // 事件组合类型为: "并且", 所有事件状态符合才触发
            case assembleTypeEnum.and: return Object.keys(stateTarget).every((key) => that.eventStatusCompare(state[key], stateTarget[key]));
            // 事件组合类型为: "或者", 一个事件状态符合就触发
            case assembleTypeEnum.or: return Object.keys(stateTarget).some((key) => that.eventStatusCompare(state[key], stateTarget[key]));
            // 事件组合类型为: "undefined", 说明只有一个事件触发者,同样使用一个状态符合就触发
            case undefined: return Object.keys(stateTarget).some((key) => that.eventStatusCompare(state[key], stateTarget[key]));
          }
        }
      }
      return eventStatusMap;
    },
    /** @name 事件行为字典
     *  @return {Object<String, Function>} {key:事件id, value:事件行为方法}
     *  @author 王汝鹏
     *  @version 2019-12-27
     */
    currentEventActionMap() {
      let that = this;
      // 表单项监听事件配置
      let { on } = this.formEventConfig;
        
      let eventActionMap = {};
      for (let e of on) {
        // 事件配置: {type:事件组合类型(并且、或者、...), emit:事件触发配置，on:事件监听配置}
        let { on } = this.eventMap[e];
        let { action } = on;

        switch(action) {
          // 显示事件
          case eventActionEnum.display: eventActionMap[e] = function(threshold) {
            // 满足触发事件条件？ T:执行显示操作 F:执行隐藏操作
            if (threshold) { that.nodeActionManager(that.formNode).display(); } 
            else { 
              that.$nextTick(() => {
                that.nodeActionManager(that.formNode).hide();
                this.toggleClearModelState == true && this.resetCurrentModel();
              }
              );  
            }
          }; break;

          // 复制事件
          case eventActionEnum.copy: eventActionMap[e] = function(threshold) {
            // 已经隐藏的元素不再处理copy事件
            if (that.isDisplay===false) return;
            // 满足触发事件条件？ T:执行copy操作
            if (threshold) { that.nodeActionManager(that.formNode).copyTo(that.parent); }
          }; break;

          // 删除事件
          case eventActionEnum.delete: eventActionMap[e] = function(threshold) {
            // 已经隐藏的元素不再处理delete事件
            if (that.isDisplay===false) return;
            // 满足触发事件条件？ T:执行delete操作
            if (threshold) { that.nodeActionManager(that.formNode).deleteForm(that.parent); }
          }; break;

          // 解禁事件
          case eventActionEnum.enable: eventActionMap[e] = function(threshold) {
            // 满足触发事件条件？ T:执行解禁操作 F:执行禁用操作,清空填写内容
            if (threshold) { that.nodeActionManager(that.formNode).enable(); }
            else { 
              that.nodeActionManager(that.formNode).disable(); 
              that.resetCurrentModel();
            }
          }; break;

          // 禁用事件
          case eventActionEnum.disable: eventActionMap[e] = function(threshold) {
            // 满足触发事件条件？ T:执行禁用操作,清空填写内容 F:执行解禁操作
            if (threshold) { 
              that.nodeActionManager(that.formNode).disable(); 
              that.resetCurrentModel();
            }
            else { that.nodeActionManager(that.formNode).enable(); }
          }; break;

          // 隐藏事件
          case eventActionEnum.hide: eventActionMap[e] = function(threshold) {
            // 满足触发事件条件？ T:执行隐藏操作 F:执行显示操作
            if (threshold) { that.$nextTick(() => that.nodeActionManager(that.formNode).hide()); } 
            else { that.nodeActionManager(that.formNode).display(); }
          }; break;
        }
      }
      return eventActionMap;
    },
  },
  created () {
    // 挂载默认值 规则: 
    // 如果当前表单项有变量名
    // 并且表单数据中没有该变量值
    console.log(this.formModels,"11111111111")
    if (this.model && (Reflect.has(this.formModels, this.model) === false)) {
      if (this.formType !== 'button') { 
        this.currentModel = this.defaultValue 
      }
      else { 
        this.currentModel = 0
        // setTimeout(()=>{
        //   this.currentModel = 0
        // },100)
      }
    }
  },
  mounted() {
    // 挂载表单事件
    // 模块组件配置结构与其它组件不同，没有event配置，所以挂载事件的时候会报错
    if (this.formType !== 'domain') {
      this._mountOnEvent();
    }
    // 表单项加载时，使用初始状态触发事件
    //   非按钮表单项直接使用当前状态触发事件
    //   按钮表单项并且状态为未点击状态，同样触发事件
    if ((this.formType !== 'button') || (this.currentModel === 0)) {
      this.$nextTick(() => { this.emitEvent("change"); });
    }
  },
  methods: {
    /** @name 清空当前表单项填写内容
     *  @author 王汝鹏
     *  @version 2020-01-18
     */
    resetCurrentModel() {
      if (this.model) {
        switch(true) {
          case Array.isArray(this.currentModel): (this.currentModel.length>0) && (this.currentModel = []); break;
          case Boolean(this.currentModel): this.currentModel = undefined; break;
        }
      }
    },
    /** @name 事件状态项比较方法
     *  @param {Any} compare 比较的值
     *  @param {Any} target 目标值
     *  @return {Boolean} 比较的结果
     *  @author 王汝鹏
     *  @version 2019-12-27
     */
    eventStatusCompare(compare, target) {
      return target === '*'  
              ?  Boolean(compare)  
              :  ((compare instanceof Array)
                  ?  (compare.includes(target))
                  :  (compare===target));
    },
    /** @name 表单项统一触发事件方法
     *  @param {String} type 触发事件的类型
     *  @author 王汝鹏
     *  @version 2019-12-27
     */
    emitEvent(type) {
      if (Reflect.has(this.currentEmitEventMap, type)) {
        this.currentEmitEventMap[type].forEach(eventId => {
          this.$bus.$emit(eventId, { form:this.formNode.id, value:this.currentModel });
        })
      }
    },
    /** @name 挂载事件方法
     *  @author 王汝鹏
     *  @version 2019-12-27
     */
    _mountOnEvent() {
      // 表单项监听事件配置
      let {on:formOns} = this.formEventConfig;
      // 遍历监听事件，挂载事件
      for (let e of formOns) {
        // 事件响应方法
        const eventAtion = (data) => {
          // 触发事件值: {form:表单项id，value:表单项值}
          let {form, value} = data;
          if (Reflect.has(this.currentEventStatusMap, e)) {
            // 更新事件状态值
            let threshold = this.currentEventStatusMap[e](form, value);
            // 触发对应事件
            this.currentEventActionMap[e](threshold);
          }
        }
        // 挂载事件、卸载事件
        this.$bus.$on(e, eventAtion);
        this.$once('hook:beforeDestroy', () => this.$bus.$off(e, eventAtion));
      }
    }
  },
  watch: {
    /** @name 监听显示/隐藏事件
     *  @description  隐藏的时候清空填写内容，传出值变化事件
     *  @param {Boolean} { true:显示，false:隐藏 }
     *  @author 王汝鹏
     *  @version 2019-12-29
     */ 
    isDisplay(newValue) { 
      if (!newValue) { 
        this.toggleClearModelState == true && this.resetCurrentModel();
      } 
    },
    /** @name 触发值变化事件
     *  @param {Any} 最新值
     *  @author 王汝鹏
     *  @version 2020-01-06
     */
    currentModel(newValue, oldValue) {
      if (this.formType === 'button') return;
      if (newValue !== oldValue) { this.emitEvent("change"); }
    }
  }
}



// WEBPACK FOOTER //
// ./src/mixins/preview_item/mix_preview_item_template_child.js